/**
 * Utility functions for detecting and parsing YouTube and Vimeo URLs
 */

/**
 * YouTube URL patterns:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 */
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;

/**
 * Vimeo URL patterns:
 * - https://vimeo.com/VIDEO_ID
 * - https://player.vimeo.com/video/VIDEO_ID
 */
const VIMEO_REGEX = /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/i;

/**
 * Check if a URL is a YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  return YOUTUBE_REGEX.test(url);
};

/**
 * Check if a URL is a Vimeo URL
 */
export const isVimeoUrl = (url: string): boolean => {
  return VIMEO_REGEX.test(url);
};

/**
 * Extract YouTube video ID from various URL formats
 */
export const extractYouTubeId = (url: string): string | null => {
  const match = url.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
};

/**
 * Extract Vimeo video ID from URL
 */
export const extractVimeoId = (url: string): string | null => {
  const match = url.match(VIMEO_REGEX);
  return match ? match[1] : null;
};

/**
 * Get embed URL and type from a video URL
 * @param url - The video URL to parse
 * @returns Object with type and embedUrl, or null if not a valid video URL
 */
export const getEmbedUrl = (
  url: string
): { type: 'youtube' | 'vimeo'; embedUrl: string } | null => {
  // Trim whitespace
  const trimmedUrl = url.trim();

  // Check YouTube
  if (isYouTubeUrl(trimmedUrl)) {
    const videoId = extractYouTubeId(trimmedUrl);
    if (videoId) {
      // Extract timestamp if present (e.g., ?t=123 or &t=123)
      const timeMatch = trimmedUrl.match(/[?&]t=(\d+)/);
      const timestamp = timeMatch ? `?start=${timeMatch[1]}` : '';

      // Use youtube-nocookie.com for privacy-enhanced mode
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}${timestamp}`,
      };
    }
  }

  // Check Vimeo
  if (isVimeoUrl(trimmedUrl)) {
    const videoId = extractVimeoId(trimmedUrl);
    if (videoId) {
      return {
        type: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
      };
    }
  }

  return null;
};

/**
 * Check if a string is a valid video URL (YouTube or Vimeo)
 */
export const isValidVideoUrl = (url: string): boolean => {
  return isYouTubeUrl(url) || isVimeoUrl(url);
};
